import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface HazardPopupProps {
  lat: number;
  lon: number;
  hazard: number;
  hazard_prev: number | null;
  hazard_next: number | null;
  interpolated_coords?: { ash_load: number; probability: number } | null;
  prev_coords?: { ash_load: number; probability: number } | null;
  next_coords?: { ash_load: number; probability: number } | null;
  hazard_curve: {
    ash_loads: number[];
    probabilities: number[];
  };
  all_percentiles_curves?: {
    [percentile: number]: {
      ash_loads: number[];
      probabilities: number[];
    };
  };
  onDownload?: (hazardCurveData: any, lat: number, lon: number, all_percentiles_curves?: any) => void;
}

const HazardPopup: React.FC<HazardPopupProps> = ({ lat, lon, hazard, hazard_prev, hazard_next, interpolated_coords, prev_coords, next_coords, hazard_curve, all_percentiles_curves, onDownload }) => {
  // Colori per i diversi percentili
  const percentileColors = [
    'rgb(255, 99, 132)',   // Rosso
    'rgb(54, 162, 235)',   // Blu
    'rgb(255, 205, 86)',   // Giallo
    'rgb(75, 192, 192)',   // Verde acqua
    'rgb(153, 102, 255)',  // Viola
    'rgb(255, 159, 64)',   // Arancione
    'rgb(199, 199, 199)',  // Grigio
    'rgb(83, 102, 255)',   // Blu scuro
    'rgb(255, 99, 255)',   // Magenta
    'rgb(99, 255, 132)'    // Verde chiaro
  ];

  // Funzione per generare le etichette dei percentili
  const getPercentileLabel = (percentile: number): string => {
    if (percentile === 0) return 'Mean';
    if (percentile === 50) return 'Median';
    
    // Genera il suffisso ordinale
    const getOrdinalSuffix = (num: number): string => {
      const lastDigit = num % 10;
      const lastTwoDigits = num % 100;
      
      if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
        return 'th';
      }
      
      switch (lastDigit) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
      }
    };
    
    return `${percentile}${getOrdinalSuffix(percentile)}`;
  };

  let datasets = [];
  
  if (all_percentiles_curves && Object.keys(all_percentiles_curves).length > 0) {
    // Crea un dataset per ogni percentile
    const percentiles = Object.keys(all_percentiles_curves)
      .map(p => parseInt(p))
      .sort((a, b) => a - b);
    
    datasets = percentiles.map((percentile, index) => {
      const curveData = all_percentiles_curves[percentile];
      const ashLoads = Array.isArray(curveData.ash_loads) ? curveData.ash_loads : [];
      const probabilities = Array.isArray(curveData.probabilities) ? curveData.probabilities : [];
      
      // Ordina i dati per ash load
      const sortedIndices = ashLoads
        .map((_, i) => i)
        .sort((a, b) => ashLoads[a] - ashLoads[b]);
      
      const sortedData = sortedIndices.map(i => ({
        x: ashLoads[i],
        y: probabilities[i]
      }));

      return {
        label: getPercentileLabel(percentile), // Usa la nuova funzione per le etichette
        data: sortedData,
        borderColor: percentileColors[index % percentileColors.length],
        backgroundColor: percentileColors[index % percentileColors.length] + '20',
        borderWidth: 2,
        fill: false,
        tension: 0.1,
        showLine: true,
        pointRadius: 3,
        pointHoverRadius: 6,
        pointBackgroundColor: percentileColors[index % percentileColors.length],
      };
    });
  } else {
    // Fallback al comportamento precedente se non ci sono dati di tutti i percentili
    const ashLoads = Array.isArray(hazard_curve.ash_loads) ? hazard_curve.ash_loads : [];
    const probabilities = Array.isArray(hazard_curve.probabilities) ? hazard_curve.probabilities : [];

    const sortedIndices = ashLoads
      .map((_, i) => i)
      .sort((a, b) => ashLoads[a] - ashLoads[b]);
    
    const sortedData = sortedIndices.map(i => ({
      x: ashLoads[i],
      y: probabilities[i]
    }));

    datasets = [{
      label: 'Hazard Curve',
      data: sortedData,
      borderColor: 'rgb(75, 192, 192)',
      backgroundColor: 'rgba(75, 192, 192, 0.2)',
      borderWidth: 2,
      fill: false,
      tension: 0.1,
      showLine: true,
      pointRadius: 3,
      pointHoverRadius: 6,
      pointBackgroundColor: 'rgb(75, 192, 192)',
    }];
  }

  const chartData = {
    datasets: datasets,
  };

  // Calcola i valori min/max per tutte le curve
  let allProbabilities: number[] = [];
  let allAshLoads: number[] = [];
  
  if (all_percentiles_curves && Object.keys(all_percentiles_curves).length > 0) {
    Object.values(all_percentiles_curves).forEach(curveData => {
      allProbabilities.push(...curveData.probabilities);
      allAshLoads.push(...curveData.ash_loads);
    });
  } else {
    allProbabilities = hazard_curve.probabilities || [];
    allAshLoads = hazard_curve.ash_loads || [];
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          font: {
            size: 10
          }
        }
      },
      title: {
        display: true,
        text: 'Hazard Curves',
      },
    },
    scales: {
      y: {
        type: 'linear' as const,
        position: 'left' as const,
        title: {
          display: true,
          text: 'Probability',
        },
        min: 0,
        max: allProbabilities.length > 0 ? Math.max(...allProbabilities) * 1.1 : 1,
        ticks: {
          maxTicksLimit: 8,
        },
        grid: {
          display: true,
        },
      },
      x: {
        type: 'logarithmic' as const,
        title: {
          display: true,
          text: 'Ash Load Intensity Measure (log scale)',
        },
        min: allAshLoads.length > 0 ? Math.min(...allAshLoads) * 0.8 : 0.001,
        max: allAshLoads.length > 0 ? Math.max(...allAshLoads) * 1.2 : 10,
        ticks: {
          maxTicksLimit: 6,
          callback: function(value: any) {
            return Number(value).toFixed(3);
          }
        },
        grid: {
          display: true,
        },
      },
    },
  };

  return (
    <div style={{ width: 620 }}>
      <div><b>Geographic Coordinates:</b> {lat.toFixed(4)}°, {lon.toFixed(4)}°</div>
      
      <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #ddd' }}>
        <div><b>Interpolated Hazard Value:</b> {hazard.toFixed(4)} kg/m²</div>
        {interpolated_coords && (
          <div style={{ fontSize: '11px', color: '#666', marginLeft: '10px', marginTop: '2px' }}>
            x (ash load) = {interpolated_coords.ash_load.toFixed(4)} kg/m², y (probability) = {interpolated_coords.probability.toFixed(4)}
          </div>
        )}
      </div>
      
      {hazard_prev !== null && (
        <div style={{ marginTop: 6 }}>
          <div><b>Hazard (lower point in intensity measure):</b> {hazard_prev.toFixed(4)} kg/m²</div>
          {prev_coords && (
            <div style={{ fontSize: '11px', color: '#666', marginLeft: '10px', marginTop: '2px' }}>
              x (ash load) = {prev_coords.ash_load.toFixed(4)} kg/m², y (probability) = {prev_coords.probability.toFixed(4)}
            </div>
          )}
        </div>
      )}
      
      {hazard_next !== null && (
        <div style={{ marginTop: 6 }}>
          <div><b>Hazard (upper point in intensity measure):</b> {hazard_next.toFixed(4)} kg/m²</div>
          {next_coords && (
            <div style={{ fontSize: '11px', color: '#666', marginLeft: '10px', marginTop: '2px' }}>
              x (ash load) = {next_coords.ash_load.toFixed(4)} kg/m², y (probability) = {next_coords.probability.toFixed(4)}
            </div>
          )}
        </div>
      )}
      
      <div style={{ marginTop: 12, height: 320, width: '100%' }}>
        <Line data={chartData} options={options} />
      </div>
      {onDownload && (
        <div style={{ marginTop: 12, textAlign: 'center', borderTop: '1px solid #eee', paddingTop: '12px' }}>
          <button
            onClick={() => onDownload(hazard_curve, lat, lon, all_percentiles_curves)}
            style={{
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
              minWidth: '200px',
              display: 'inline-block'
            }}
          >
            📊 Download Curve Data {all_percentiles_curves && Object.keys(all_percentiles_curves).length > 0 ? '(All Percentiles)' : ''}
          </button>
        </div>
      )}
    </div>
  );
};

export default HazardPopup;